import { faCommentPlus, faPen, faPenToSquare } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useState } from 'react'
import TwinIcon, { TwinIconToolTip } from '../../../../../../../baseComponents/TwinIcon'
import { displayFormatedHourFromMins } from '../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { WCalTurnsByEmployeeTy, WCalTurnWeekTy, WCalTurnScheduleDictTY, WCalTurnSingleSchedule } from '../types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { RenderSchedule } from '../../../../../../../specificComponents/WCal/WCalTurns/RenderSchedule'
import { getConfigParam, getEmployeeParams, getUserPermissions } from '../../../../../../../utils/reducers/getters'
import TwinToolTip from '../../../../../../../baseComponents/TwinToolTip'
import PermissionChecker from '../../../../../../../baseComponents/PermissionChecker'
import ModalCEWCalTurn from '../ModalCEWCalTurn'
import ModalEditTurnNotes from './ModalEditTurnNotes'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { DividedByTurnsTY } from '@teinor/erp/types/company/userInner/employee/employeeTimetable'

interface SingleEmployeeTurnProps {
    employeeData: WCalTurnsByEmployeeTy
    week: WCalTurnWeekTy
    getData: () => Promise<void>
    selectedWorkingCenter: string
}

const SingleEmployeeTurn: React.FC<SingleEmployeeTurnProps> = ({ employeeData, week, getData, selectedWorkingCenter }) => {
    const { openedModalSchedule, setOpenedModalSchedule } = useSingleEmployeeTurnLogic()
    const schedules: { [key: string]: WCalTurnScheduleDictTY } = employeeData?.dividedByTurns
    const renderSchedule: JSX.Element[] = []

    for (const key in employeeData?.baseJourney) {
        const baseJourney = employeeData?.baseJourney[key]
        const isTurnDay = baseJourney?.isTurnDay
        renderSchedule.push(
            <RenderTurnScheduleContainer baseJourney={baseJourney} key={key} isTurnDay={isTurnDay} day={key} selectedWorkingCenter={selectedWorkingCenter} dividedByTurns={employeeData?.dividedByTurns?.[key] || null} getData={getData} />
        )
    }

    return (
        <div className='flex flex-auto items-center px-5 border-b border-gray-EE wCalTurn_single_employee_turn'>
            <EmployeeInfoCell employeeData={employeeData} setOpenedModalSchedule={setOpenedModalSchedule} />
            <div className='flex flex-auto h-full items-stretch w-1'>
                {renderSchedule}
            </div>
            {openedModalSchedule ?
                <ModalCEWCalTurn  {...{ setOpenedModalSchedule, schedules, week, getData, employeeData: employeeData.employee, selectedWorkingCenter, baseJourney: employeeData.baseJourney }} />
                : null}
        </div>
    )
}

const useSingleEmployeeTurnLogic = () => {
    const [openedModalSchedule, setOpenedModalSchedule] = useState<true | null>(null)
    return { openedModalSchedule, setOpenedModalSchedule }
}

interface EmployeeInfoCellProps {
    employeeData: WCalTurnsByEmployeeTy
    setOpenedModalSchedule: (value: React.SetStateAction<true | null>) => void
}

const EmployeeInfoCell: React.FC<EmployeeInfoCellProps> = ({ employeeData, setOpenedModalSchedule }) => {
    let totalHours = 0
    let totalOvertime = 0
    let totalJustifiedHours = 0
    for (const key in employeeData.baseJourney) {
        const journey = employeeData.baseJourney[key]
        totalHours += parseFloat(String(journey?.dayMins || '0'))
        totalOvertime += parseFloat(String(journey?.overtimeMins || '0'))
        totalJustifiedHours += parseFloat(String(journey?.justifiedMins || '0'))
    }
    return (
        <div className='flex items-center turn_employee_cell'>
            <img src={employeeData?.employee?.profile_image || '/user-png.png'} className='image_employee rounded-full w-50 h-50' alt='' />
            <div className='ml-10'>
                <div className='regular14'>{employeeData?.employee?.fullname_short}</div>
                <div className='light14 text-gray-51'><TwinTrans transKey='totalHours'>Total horas</TwinTrans>: {displayFormatedHourFromMins(totalHours)}<TwinTrans transKey='hLowerCase'>h</TwinTrans></div>
                {totalOvertime ? <div className='light14 text-gray-51'><TwinTrans transKey='overtime'>Horas extra</TwinTrans>: {displayFormatedHourFromMins(totalOvertime)}<TwinTrans transKey='hLowerCase'>h</TwinTrans></div> : null}
                {totalJustifiedHours ? <div className='light14 text-gray-51'><TwinTrans transKey='justifiedHours'>Horas justificadas</TwinTrans>: {displayFormatedHourFromMins(totalJustifiedHours)}<TwinTrans transKey='hLowerCase'>h</TwinTrans></div> : null}
            </div>
            <PermissionChecker userPermissions={getUserPermissions('WCal.turn')} permission='update'>
                <div className='ml-auto cursor-pointer hover:text-green-43' onClick={() => setOpenedModalSchedule(true)}>
                    <TwinIcon icon={faPenToSquare} className='mr-6' />
                </div>
            </PermissionChecker>
        </div>
    )
}


interface RenderTurnScheduleBaseProps {
    baseJourney: WCalTurnSingleSchedule
    isTurnDay: boolean
    selectedWorkingCenter: string
    dividedByTurns: WCalTurnScheduleDictTY
    getData: () => Promise<void>
}

interface RenderTurnScheduleBasePropsWithDay extends RenderTurnScheduleBaseProps {
    day: string
}

const RenderTurnScheduleContainer: React.FC<RenderTurnScheduleBasePropsWithDay> = ({ baseJourney, isTurnDay, day, selectedWorkingCenter, dividedByTurns, getData }) => {
    const { t } = useTwinTranslation()
    const punchInWithWorkingCenter = getConfigParam('punchInWithWorkingCenter') === 'on'
    const turnDictionaryByType: TwinDictionary = {
        'noWorkDay': {
            color: 'white',
            title: ''
        },
        'normalDay': {
            color: '#F2F9FA',
            title: t('baseSchedule', 'Horario base')
        },
        'turnDayFlexible': {
            color: '#E2F3FF',
            title: t('flexibleSchedule', 'Horario flexible')
        },
        'turnDay': {
            color: '#E0FCF2',
            title: t('fixSchedule', 'Horario fijo')
        },
        'holidayDay': {
            color: '#FFF2E1',
            title: t('holidays', 'Vacaciones')
        },
        'absenceDay': {
            color: '#FFE2DB',
            title: t('workLeave', 'Baja laboral')
        },
        'freeDay': {
            color: '#FBEDFF',
            title: t('freeDay', 'Día festivo')
        },
        'specialDay': {
            color: '#DEF7FB',
            title: t('specialDay', 'Día especial')
        },
        'overtime': {
            color: '#E0E2FC',
            title: t('overtime', 'Horas extra')
        },
    }
    return (
            <RenderDividedTurnScheduleCell baseJourney={baseJourney} turnDictionaryByType={turnDictionaryByType} isTurnDay={isTurnDay} selectedWorkingCenter={selectedWorkingCenter} punchInWithWorkingCenter={punchInWithWorkingCenter} dividedByTurns={dividedByTurns} day={day} getData={getData}/>
    )
}

interface RenderTurnScheduleCellProps extends RenderTurnScheduleBasePropsWithDay {
    turnDictionaryByType: TwinDictionary
    punchInWithWorkingCenter?: boolean
}

const RenderDividedTurnScheduleCell: React.FC<RenderTurnScheduleCellProps> = ({ baseJourney, turnDictionaryByType, isTurnDay, day, selectedWorkingCenter, punchInWithWorkingCenter, dividedByTurns, getData }) => {
    const renderThis: JSX.Element[] = []
    let myType = baseJourney?.type
    if (myType === 'holidayDay') {
        renderThis.push(<HolidayTurn baseJourney={baseJourney} key={'holiday' + day} turnDictionaryByType={turnDictionaryByType} />)
    }
    if (myType === 'absenceDay' || myType === 'absenceJustifiedDay' || baseJourney?.absenceWorkingTime) {
        renderThis.push(<AbsenceTurn baseJourney={baseJourney} key={myType + day} turnDictionaryByType={turnDictionaryByType} />)
    }
    if (isTurnDay) {
        if (baseJourney?.journey?.workingTime?.length || punchInWithWorkingCenter) {
            if (dividedByTurns && punchInWithWorkingCenter) {
                const schedules = ScheduleWCTurnRenderer({ baseJourney, dividedByTurns, isAllDayEvent: false, isTurnDay, selectedWorkingCenter, turnDictionaryByType, getData })
                if (schedules) {
                    for (const element of schedules) {
                        renderThis.push(element)
                    }
                }
            } else {
                renderThis.push(<ScheduleTurn baseJourney={baseJourney} key={myType + 'turn' + day} turnDictionaryByType={turnDictionaryByType} getData={getData}/>)
            }
        }
    } else {
        if (baseJourney?.journey?.workingTime && baseJourney?.journey?.workingTime.length) {
            const withAbsenceAllDay = baseJourney?.absenceWorkingTime && (baseJourney?.absenceWorkingTime?.[0]?.[0] === 0 && baseJourney?.absenceWorkingTime?.[0]?.[1] === 0)
            if (!withAbsenceAllDay) {
                renderThis.push(<BaseScheduleTurn baseJourney={baseJourney} turnDictionaryByType={turnDictionaryByType} key={myType + 'scheduleTurn' + day} />)
            }
        }
    }
    if (baseJourney?.journey?.overtimePosition && baseJourney.journey?.overtimePosition.length) {
        renderThis.push(<OvertimeScheduleTurn baseJourney={baseJourney} key={myType + 'overtime' + day} turnDictionaryByType={turnDictionaryByType} />)
    }

    return (
        <div className={'single_schedule_cell p-5 flex flex-col border-l border-gray-EE ' + (renderThis.length > 1 ? 'divided_cell' : '')}>
            {renderThis}
        </div>
    )
}

interface ScheduleWCTurnProps extends RenderTurnScheduleBaseProps {
    isAllDayEvent: boolean
    turnDictionaryByType: TwinDictionary
}
const ScheduleWCTurnRenderer = ({ dividedByTurns, isAllDayEvent, selectedWorkingCenter, turnDictionaryByType, getData  }: ScheduleWCTurnProps) => {
    const renderThis: JSX.Element[] = []
    const myOrderedDivided = []
    for (const key in dividedByTurns) {
        const turn = dividedByTurns[key as any]
        if (turn.schedule?.workingTime?.length && turn?.schedule?.WorkingCenterId) {
            if (selectedWorkingCenter) {
                if (parseInt(selectedWorkingCenter) === parseInt(String(turn?.schedule?.WorkingCenterId))) {
                    myOrderedDivided.push(turn)
                }
            } else {
                myOrderedDivided.push(turn)
            }
        }
    }
    if (!myOrderedDivided.length) {
        return null
    }
    if (myOrderedDivided.length) {
        myOrderedDivided.sort((a, b) => (a.schedule?.workingTime?.[0]?.[0] || 0) - (b.schedule?.workingTime?.[0]?.[0] || 0))
        for (const turn of myOrderedDivided) {
            renderThis.push(
                <SingleTurnWithWorkingCenter color={turnDictionaryByType['turnDay'].color} turn={turn.schedule} key={turn.TurnId} isAllDayEvent={isAllDayEvent} getData={getData} />
            )
        }
    }
    return renderThis
}


interface HolidayTurnProps {
    baseJourney: WCalTurnSingleSchedule
    turnDictionaryByType: TwinDictionary
}

const HolidayTurn: React.FC<HolidayTurnProps> = ({ baseJourney, turnDictionaryByType}) => {
    const isHalfDay = baseJourney?.isHalfDay
    const myTurnDict = turnDictionaryByType['holidayDay']
    return (
        <SingleTurn color={myTurnDict.color} title={myTurnDict.title} className='divided_turn' key='holidayDay'>
            {isHalfDay ? <div className='light12' key={new Date().toISOString()}>
                <TwinTrans transKey='halfDay'>Medio día</TwinTrans>
            </div> : null}
        </SingleTurn>
    )
}

interface AbsenceTurnProps {
    baseJourney: WCalTurnSingleSchedule
    turnDictionaryByType: TwinDictionary
}

const AbsenceTurn: React.FC<AbsenceTurnProps> = ({ baseJourney, turnDictionaryByType}) => {
    const myTurnDict = turnDictionaryByType['absenceDay']
    return (
        <SingleTurn color={myTurnDict.color} title={myTurnDict.title} className='divided_turn' key='absenceDay'>
            <RenderSchedule workingTime={baseJourney?.absenceWorkingTime || []} />
        </SingleTurn>
    )
}
interface ScheduleTurnProps {
    baseJourney: WCalTurnSingleSchedule
    turnDictionaryByType: TwinDictionary
    getData: () => Promise<void>
}

const ScheduleTurn: React.FC<ScheduleTurnProps> = ({ baseJourney, turnDictionaryByType, getData }) => {
    let myType = 'turnDay'
    if (baseJourney.journey?.flexible) {
        myType = 'turnDayFlexible'
    }
    const myTurnDict = turnDictionaryByType[myType]

    return (
        <SingleTurn color={myTurnDict.color} title={myTurnDict.title} className='divided_turn' key='scheduleTurnDay' getData={getData} turn={baseJourney?.dividedByTurns?.[0]}>
            <RenderSchedule workingTime={baseJourney?.journey?.workingTime || []} />
        </SingleTurn>
    )
}

interface BaseScheduleTurnProps {
    baseJourney: WCalTurnSingleSchedule
    turnDictionaryByType: TwinDictionary
}

const BaseScheduleTurn: React.FC<BaseScheduleTurnProps> = ({ baseJourney, turnDictionaryByType }) => {
    let myType = 'normalDay'
    const myBaseType = baseJourney.type
    if (turnDictionaryByType[myBaseType] && (myBaseType !== 'holidayDay' && myBaseType !== 'absenceDay' && myBaseType !== 'absenceJustifiedDay')) {
        myType = myBaseType
    }
    const myTurnDict = turnDictionaryByType[myType]
    return (
        <SingleTurn color={myTurnDict.color} title={myTurnDict.title} className='divided_turn' key='baseScheduleDay'>
            <RenderSchedule workingTime={baseJourney?.journey?.workingTime || []} />
        </SingleTurn>
    )
}

interface OvertimeScheduleTurnProps {
    baseJourney: WCalTurnSingleSchedule
    turnDictionaryByType: TwinDictionary
}

const OvertimeScheduleTurn: React.FC<OvertimeScheduleTurnProps> = ({ baseJourney, turnDictionaryByType }) => {
    const myTurnDict = turnDictionaryByType['overtime']

    return (
        <SingleTurn color={myTurnDict.color} title={myTurnDict.title} className='divided_turn' key='overtimeDay'>
            <RenderSchedule workingTime={baseJourney?.originalOvertime || []} />
        </SingleTurn>
    )
}

interface SingleTurnProps extends SingleTurnBoxProps {
    title: string
    getData?: () => Promise<void>
}

const SingleTurn: React.FC<SingleTurnProps> = ({ title, children, turn, ...rest }) => {
    return (
        <SingleTurnBox {...rest} turn={turn}>
            <div className='w-full flex items-center gap-2'>
                <div className='flex item-center flex-auto w-1' >
                    <span className='medium12'>{title}</span>
                </div>
                {turn?.notes !== undefined && turn?.TurnId && rest.getData ? <TurnNotes turn={turn} getData={rest.getData} /> : null}
            </div>
            {children}
        </SingleTurnBox>
    )
}
interface SingleTurnWithWorkingCenterProps extends SingleTurnBoxProps {
    isAllDayEvent: boolean
    getData?: () => Promise<void>
}

const SingleTurnWithWorkingCenter: React.FC<SingleTurnWithWorkingCenterProps> = ({ color, className, isAllDayEvent, getData, turn }) => {
    if (!turn) {
        return null
    }
    const { TurnId, WorkingCenterId, workingCenterName, workingTime, notes } = turn
    const myId = 'workingCenter_' + TurnId
    const myTurnConfig = getEmployeeParams()?.['wcal_turn_config'] ? JSON.parse(getEmployeeParams()?.['wcal_turn_config'] || '') : null
    let myColor = color
    if (WorkingCenterId && myTurnConfig && myTurnConfig?.colors && myTurnConfig?.colors?.[WorkingCenterId]) {
        myColor = myTurnConfig?.colors?.[WorkingCenterId]
    }
    return (
        <SingleTurnBox className={'working_center_turn ' + (className || '')} turn={turn} color={myColor}>
            <div className='w-full flex items-center gap-2'>
                <div className='flex item-center flex-auto w-1' id={myId}>
                    <TwinToolTip place='top' anchorSelect={'#' + myId} className='' positionStrategy='fixed' content={workingCenterName} />
                    <div className='medium12'><TwinTrans transKey='schedule'>Horario</TwinTrans></div>
                    <div className='medium12 mx-5'>-</div>
                    <div className='medium12 twin_elipsis'>{workingCenterName?.slice(0, 15)}</div>
                </div>
                {notes !== undefined && TurnId && getData ? <TurnNotes turn={turn} getData={getData} /> : null}
            </div>
            {!isAllDayEvent ? <RenderSchedule workingTime={workingTime || []} /> : null}
        </SingleTurnBox>
    )
}

interface SingleTurnBoxProps {
    color: string,
    children?: React.ReactNode
    className?: string
    turn?: DividedByTurnsTY
}

const SingleTurnBox: React.FC<SingleTurnBoxProps> = ({ children, color, className, turn }) => {
    return (
        <div className={'flex flex-auto px-10 single_employee_turn flex-col py-7 ' + (className || '')} style={{ backgroundColor: color }} id={'singleTurn'+turn?.TurnId || ''}>
            {children}
        </div>
    )
}

interface TurnNotesProps {
    turn?: DividedByTurnsTY
    getData: () => Promise<void>
}

const TurnNotes: React.FC<TurnNotesProps> = ({ turn, getData }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    if (!turn) {
        return null
    }
    let myToolTip = {
        message: t('addNotes', 'Añadir notas'),
        icon: faCommentPlus
    }
    if (turn?.notes) {
        myToolTip = {
            message: turn.notes,
            icon: faPen
        }
    }
    return (
        <Fragment>
            <TwinIconToolTip className='ml-auto ' id={'turnNotes_' + turn.TurnId} onClick={() => setOpenModal({ type: 'edit', allRowData: { turn } })} classNameTooltip='max-w-300' {...myToolTip} />
            {openModal?.type === 'edit' ?
                <ModalEditTurnNotes allRowData={openModal.allRowData} setOpenModal={setOpenModal} getData={getData} />
                : null
            }
        </Fragment>
    )
}


export default SingleEmployeeTurn